/**
 * Termos de Uso, Política de Privacidade e LGPD da FórmulaMed — texto e dados da empresa num lugar só.
 *
 * Decisões do usuário (15/09/2026): serviço "FórmulaMed"; Fórmula 4IA Soluções Ltda. com endereço
 * e foro de Caldas Novas/GO; Encarregado Bruno Borges Pereira Vale.
 *
 * O texto descreve o que o sistema FAZ, conferido no código e na produção naquele dia:
 * servidores na Hostinger (São Paulo), IA ativa só Gemini e OpenAI, Asaas como gateway, WhatsApp pela
 * UAZAPI, teleconsulta no meet.jit.si, CEP no ViaCEP, cartão guardado só com bandeira e 4 dígitos,
 * backups com rotação de 30, conversas da assistente em 90 dias, avisos em 7 dias. Mudou algo disso:
 * mude aqui, suba a versão e o aceite do autocadastro passa a pedir a nova.
 */

export const EMPRESA = {
    razaoSocial: 'Fórmula 4IA Soluções Ltda.',
    cnpj: '53.214.179/0001-46',
    endereco: 'C 8, s/n, Quadra 45, Lote 02, Sala 02, Estância Itanhangá, Caldas Novas/GO, CEP 75680-390',
    whatsapp: '(64) 3559-0229',
    telefone: '(64) 2018-1808',
    email: 'bruno@formulaia.ai',
    foro: 'Caldas Novas/GO',
} as const;

export const PRODUTO = {
    nome: 'Fórmula MED',
    site: 'https://app.formulamed.com.br',
} as const;

export const ENCARREGADO = {
    nome: 'Bruno Borges Pereira Vale',
    email: 'bruno@formulaia.ai',
    telefone: '(64) 3559-0229',
} as const;

export type IdDoDocumento = 'termos' | 'privacidade' | 'lgpd';

export type Bloco =
    | { tipo: 'p'; texto: string }
    | { tipo: 'lista'; itens: string[] }
    | { tipo: 'tabela'; colunas: string[]; linhas: string[][] };

export interface Secao {
    id: string;
    titulo: string;
    blocos: Bloco[];
}

export interface DocumentoLegal {
    id: IdDoDocumento;
    caminho: string;
    titulo: string;
    resumo: string;
    /** Vai para `aceites_de_termos.versao`: trocar o texto sem trocar a versão apaga a prova do que foi aceito. */
    versao: string;
    vigenteDesde: string;
    secoes: Secao[];
}

const VIGENCIA = '15 de setembro de 2026';
const VERSAO = '2026-09-15';

const URL_TERMOS = `${PRODUTO.site}/termos-de-uso`;
const URL_PRIVACIDADE = `${PRODUTO.site}/politica-de-privacidade`;
const URL_LGPD = `${PRODUTO.site}/lgpd`;

const QUALIFICACAO = `${EMPRESA.razaoSocial}, inscrita no CNPJ sob o nº ${EMPRESA.cnpj}, com sede em ${EMPRESA.endereco}`;

const CONTATO: Bloco = {
    tipo: 'lista',
    itens: [
        `${EMPRESA.razaoSocial} — CNPJ ${EMPRESA.cnpj}`,
        `Endereço: ${EMPRESA.endereco}`,
        `WhatsApp: ${EMPRESA.whatsapp} · Telefone: ${EMPRESA.telefone}`,
        `E-mail: ${EMPRESA.email}`,
        `Encarregado pelo tratamento de dados: ${ENCARREGADO.nome} — ${ENCARREGADO.email}`,
    ],
};

/* Os dois prazos do fim do contrato aparecem nos Termos, na Política e no Acordo: um texto só. */
const FIM_DO_CONTRATO = 'Após o encerramento, a Clínica tem 30 dias para pedir a exportação do Conteúdo da Clínica. '
    + 'Depois desse prazo, os dados são eliminados dos sistemas ativos em até 60 dias e das cópias de segurança em até 30 dias depois disso, '
    + 'salvo o que a lei nos obrigue a guardar.';

const FORNECEDORES: string[][] = [
    ['Hostinger', 'Servidores onde a plataforma e o banco de dados funcionam, no Brasil (São Paulo)', 'Sim, armazena todos os dados da plataforma'],
    ['Google', 'Entrar com Google, Google Agenda, e-mails da conta (Google Workspace) e recursos de IA (Gemini), inclusive a conversa por voz', 'Sim, quando a clínica usa a IA ou a Agenda; na Agenda, só o primeiro nome do paciente'],
    ['OpenAI', 'Recursos de IA do assistente e da base de conhecimento da clínica', 'Sim, quando a clínica usa a IA'],
    ['UAZAPI', 'Conexão do WhatsApp da clínica para enviar e receber mensagens', 'Sim, as mensagens trocadas pelo WhatsApp conectado'],
    ['Asaas', 'Pagamentos e cobrança recorrente da assinatura', 'Não'],
    ['Jitsi Meet (8x8)', 'Salas de vídeo das teleconsultas, abertas diretamente no serviço', 'Sim, o áudio e o vídeo da teleconsulta'],
    ['ViaCEP', 'Preenchimento do endereço a partir do CEP digitado', 'Só o CEP'],
    ['unpkg e Google Fonts', 'Entrega de ícones e fontes da interface', 'Não; recebem apenas o endereço IP do navegador'],
];

const TERMOS: DocumentoLegal = {
    id: 'termos',
    caminho: '/termos-de-uso',
    titulo: 'Termos de Uso',
    resumo: `As regras para usar a ${PRODUTO.nome}: conta, planos e teste grátis, pagamento, uso de IA, integrações, responsabilidades e encerramento.`,
    versao: VERSAO,
    vigenteDesde: VIGENCIA,
    secoes: [
        {
            id: 'quem-somos',
            titulo: 'Quem somos e o que são estes Termos',
            blocos: [
                { tipo: 'p', texto: `A ${PRODUTO.nome} é uma plataforma on-line de gestão para clínicas de estética e saúde — agenda, prontuário, atendimento, CRM, financeiro, estoque, comunicação com pacientes e assistente de inteligência artificial — oferecida pela ${QUALIFICACAO} (“Fórmula 4IA”, “nós”).` },
                { tipo: 'p', texto: `Estes Termos de Uso regulam o acesso e o uso da plataforma em ${PRODUTO.site} e nos demais endereços em que ela for disponibilizada. Ao criar uma conta, aceitar um convite ou usar a plataforma, você declara que leu e concorda com estes Termos, com a Política de Privacidade (${URL_PRIVACIDADE}) e com a página LGPD e Tratamento de Dados (${URL_LGPD}), que fazem parte deles.` },
                { tipo: 'p', texto: 'Se você aceita em nome de uma clínica ou empresa, declara ter poderes para vinculá-la a estes Termos.' },
            ],
        },
        {
            id: 'definicoes',
            titulo: 'Definições',
            blocos: [
                {
                    tipo: 'lista',
                    itens: [
                        'Clínica: a pessoa física ou jurídica que contrata a plataforma e cadastra a sua equipe.',
                        'Usuário: cada pessoa com acesso à conta da Clínica — proprietário, profissional, recepção ou outro papel definido pela Clínica.',
                        'Titular: a pessoa a quem os dados pessoais se referem, como pacientes, leads e usuários.',
                        'Conteúdo da Clínica: tudo o que a Clínica e os seus Usuários inserem na plataforma, incluindo cadastros, agendamentos, prontuários, fichas, fotos, documentos, mensagens e dados financeiros.',
                        'Plano: a modalidade de assinatura escolhida, com os seus recursos, limites e preço.',
                    ],
                },
            ],
        },
        {
            id: 'conta',
            titulo: 'Conta, acesso e segurança',
            blocos: [
                {
                    tipo: 'lista',
                    itens: [
                        'O acesso é feito com e-mail e senha ou com uma conta Google. As credenciais são pessoais e intransferíveis: cada pessoa deve ter o próprio acesso.',
                        'A Clínica escolhe quem convida e qual papel cada Usuário tem, e responde pelos atos praticados com as contas da sua equipe. Ao desligar alguém, a Clínica deve desativar o acesso dessa pessoa.',
                        `Avise-nos imediatamente pelo e-mail ${EMPRESA.email} se suspeitar de uso indevido da sua conta.`,
                        'Nossa equipe não acessa o Conteúdo da Clínica no dia a dia. Quando o suporte precisa ver o que a Clínica vê, o acesso é feito em modo somente leitura, por no máximo 30 minutos, com motivo registrado e trilha de auditoria.',
                    ],
                },
            ],
        },
        {
            id: 'planos',
            titulo: 'Planos, período de teste e pagamento',
            blocos: [
                {
                    tipo: 'lista',
                    itens: [
                        'O Plano, o preço e a periodicidade são os informados no momento da contratação.',
                        'Quando oferecido, o período de teste gratuito dura 14 dias e exige o cadastro de um cartão de crédito. Se o teste não for cancelado até o fim desse prazo, a primeira mensalidade é cobrada automaticamente no cartão no 14º dia, e as seguintes a cada mês.',
                        'Na contratação por Pix Automático, a primeira mensalidade é paga no ato, e as seguintes são debitadas mensalmente conforme a autorização dada no aplicativo do seu banco.',
                        'O teste pode ser cancelado a qualquer momento em Configurações › Assinatura, sem cobrança.',
                        'Os pagamentos são processados pelo Asaas, instituição de pagamento parceira. Não armazenamos o número completo do cartão nem o código de segurança; guardamos apenas a bandeira e os quatro últimos dígitos, para você reconhecer o cartão cadastrado.',
                        'Em caso de atraso, a Clínica é avisada na plataforma e, persistindo a falta de pagamento, a conta passa ao modo somente leitura até a regularização: os dados continuam disponíveis para consulta, mas não é possível criar nem alterar registros.',
                        'Reajustes de preço são comunicados com pelo menos 30 dias de antecedência e valem a partir do ciclo seguinte.',
                        'A Clínica pode cancelar a assinatura a qualquer momento. O cancelamento vale ao fim do período já pago, sem reembolso proporcional, ressalvados os direitos previstos em lei.',
                    ],
                },
            ],
        },
        {
            id: 'uso',
            titulo: 'Uso permitido',
            blocos: [
                { tipo: 'p', texto: 'A Clínica e os Usuários se comprometem a usar a plataforma de forma lícita e de acordo com estes Termos. É proibido:' },
                {
                    tipo: 'lista',
                    itens: [
                        'inserir dados pessoais sem base legal adequada ou sem as informações devidas aos titulares;',
                        'enviar mensagens em massa não solicitadas, spam ou conteúdo enganoso pelos canais de comunicação;',
                        'tentar acessar dados de outras clínicas, burlar controles de acesso ou testar vulnerabilidades sem autorização por escrito;',
                        'copiar, modificar, fazer engenharia reversa, revender ou sublicenciar a plataforma;',
                        'usar a plataforma para atividade ilegal ou em desacordo com as normas dos conselhos profissionais, inclusive as de publicidade na área da saúde.',
                    ],
                },
            ],
        },
        {
            id: 'clinica',
            titulo: 'Responsabilidades da Clínica',
            blocos: [
                {
                    tipo: 'lista',
                    itens: [
                        'Manter corretos e atualizados os dados que insere, e decidir com base neles.',
                        'Cumprir as normas aplicáveis à sua atividade, inclusive as de guarda e sigilo do prontuário e as dos conselhos profissionais. A Clínica é a responsável legal pelo prontuário dos seus pacientes.',
                        `Escolher a base legal e informar os pacientes sobre o tratamento dos dados deles, como controladora (veja ${URL_LGPD}).`,
                        'Garantir que tem autorização para contatar pacientes e leads pelos canais conectados, e respeitar os pedidos para não receber mensagens.',
                        'Revisar documentos, orçamentos, mensagens e sugestões gerados com apoio da plataforma antes de usá-los.',
                    ],
                },
            ],
        },
        {
            id: 'ia',
            titulo: 'Recursos de inteligência artificial',
            blocos: [
                {
                    tipo: 'lista',
                    itens: [
                        'A plataforma oferece um assistente e outros recursos de inteligência artificial que sugerem textos, respostas, resumos e ações.',
                        'As respostas da IA podem conter erros e não substituem o julgamento do profissional. Nenhuma decisão clínica deve se basear apenas nelas.',
                        'Ações sugeridas pela IA que alteram dados — como criar um agendamento ou enviar uma mensagem — só são executadas depois da confirmação explícita do Usuário, com os campos à vista.',
                        'Quando a Clínica ativa um agente de atendimento, as respostas enviadas aos contatos seguem as instruções definidas por ela, que responde por elas e pode desativar o agente a qualquer momento.',
                        'Para funcionar, o conteúdo do pedido e os dados necessários para respondê-lo são enviados a provedores de IA contratados, descritos na Política de Privacidade.',
                        'O uso de IA pode ter um limite mensal, conforme o Plano.',
                    ],
                },
            ],
        },
        {
            id: 'terceiros',
            titulo: 'Integrações e serviços de terceiros',
            blocos: [
                {
                    tipo: 'lista',
                    itens: [
                        'Algumas funções dependem de serviços de terceiros escolhidos e conectados pela Clínica ou pelo Usuário, como WhatsApp, Google Agenda, gateway de pagamento e sala de teleconsulta. O uso desses serviços também segue os termos de cada fornecedor.',
                        'A conexão de WhatsApp é feita por provedor de integração de terceiros. A Meta pode limitar ou bloquear números usados em desacordo com as políticas do WhatsApp; a Clínica é responsável pelo uso que faz do seu número.',
                        'A integração com o Google Agenda é de mão única: cada profissional conecta a própria conta, e a plataforma grava na agenda principal dele os atendimentos e bloqueios, apenas com o primeiro nome do paciente. Desconectar apaga os eventos futuros criados pela plataforma.',
                        'As salas de teleconsulta usam o Jitsi Meet (meet.jit.si), serviço de videoconferência de terceiros. O áudio e o vídeo trafegam pelo Jitsi, não pelos nossos servidores.',
                        'Webhooks e chaves de API enviam ou entregam dados da Clínica a sistemas escolhidos e configurados por ela. Esses envios seguem as instruções da Clínica, que responde pela escolha do destino e pelo tratamento que ele faz dos dados.',
                        'Não respondemos por indisponibilidade, mudanças ou falhas de serviços de terceiros, mas trabalharemos para restabelecer as funções afetadas.',
                    ],
                },
            ],
        },
        {
            id: 'disponibilidade',
            titulo: 'Disponibilidade e suporte',
            blocos: [
                {
                    tipo: 'lista',
                    itens: [
                        'Empregamos esforços razoáveis para manter a plataforma disponível e segura, mas não garantimos funcionamento ininterrupto ou livre de erros.',
                        'Manutenções programadas são feitas preferencialmente fora do horário comercial e, quando possível, avisadas com antecedência.',
                        `O suporte é prestado pelo WhatsApp ${EMPRESA.whatsapp}, pelo telefone ${EMPRESA.telefone} e pelo e-mail ${EMPRESA.email}.`,
                        'Fazemos cópias de segurança diárias do banco de dados. Elas servem para recuperação de desastres e não substituem a exportação que a Clínica deve fazer dos registros que precisa guardar.',
                    ],
                },
            ],
        },
        {
            id: 'propriedade',
            titulo: 'Propriedade intelectual e Conteúdo da Clínica',
            blocos: [
                {
                    tipo: 'lista',
                    itens: [
                        'A plataforma, a sua marca, o código, o layout e a documentação pertencem à Fórmula 4IA. A assinatura dá à Clínica uma licença de uso limitada, não exclusiva e intransferível, enquanto durar a contratação.',
                        'O Conteúdo da Clínica pertence à Clínica. Tratamos esse conteúdo apenas para prestar o serviço, conforme as instruções da Clínica e a legislação.',
                        'Sugestões de melhoria enviadas por você podem ser usadas por nós livremente, sem obrigação de pagamento.',
                    ],
                },
            ],
        },
        {
            id: 'dados',
            titulo: 'Proteção de dados pessoais',
            blocos: [
                { tipo: 'p', texto: `O tratamento de dados pessoais segue a Lei Geral de Proteção de Dados (Lei nº 13.709/2018). Sobre os dados de pacientes e leads inseridos na plataforma, a Clínica é a controladora e a Fórmula 4IA atua como operadora, nos termos do Acordo de Tratamento de Dados publicado em ${URL_LGPD}, que integra estes Termos. Sobre os dados de cadastro, cobrança e uso da conta, a Fórmula 4IA é a controladora, conforme a Política de Privacidade (${URL_PRIVACIDADE}).` },
            ],
        },
        {
            id: 'responsabilidade',
            titulo: 'Limitação de responsabilidade',
            blocos: [
                {
                    tipo: 'lista',
                    itens: [
                        'A Fórmula 4IA não responde por decisões clínicas, administrativas ou financeiras tomadas pela Clínica com base na plataforma, nem por danos causados por uso indevido de credenciais, por informações incorretas inseridas pelos Usuários ou por falhas de serviços de terceiros.',
                        'Não respondemos por lucros cessantes, perda de receita ou danos indiretos.',
                        'Nos limites permitidos pela lei, a responsabilidade total da Fórmula 4IA fica limitada ao valor pago pela Clínica nos 12 meses anteriores ao fato que originou o dano.',
                        'Essas limitações não se aplicam a casos de dolo ou culpa grave, nem afastam direitos que a lei não permita limitar.',
                    ],
                },
            ],
        },
        {
            id: 'encerramento',
            titulo: 'Suspensão e encerramento',
            blocos: [
                {
                    tipo: 'lista',
                    itens: [
                        'A Clínica pode encerrar a contratação a qualquer momento, pela plataforma ou pelos canais de suporte.',
                        'Podemos suspender ou encerrar o acesso em caso de violação destes Termos, de uso que coloque em risco a plataforma ou terceiros, ou de inadimplência não regularizada, com aviso prévio sempre que possível.',
                        FIM_DO_CONTRATO,
                    ],
                },
            ],
        },
        {
            id: 'alteracoes',
            titulo: 'Alterações destes Termos',
            blocos: [
                { tipo: 'p', texto: 'Podemos atualizar estes Termos para refletir mudanças na plataforma ou na legislação. A versão e a data de vigência ficam no topo desta página. Mudanças relevantes são avisadas com pelo menos 30 dias de antecedência, pela plataforma ou por e-mail, e podem exigir novo aceite para continuar usando o serviço.' },
            ],
        },
        {
            id: 'foro',
            titulo: 'Lei aplicável e foro',
            blocos: [
                { tipo: 'p', texto: `Estes Termos são regidos pelas leis da República Federativa do Brasil. Fica eleito o foro da Comarca de ${EMPRESA.foro} para resolver qualquer controvérsia, ressalvado o direito do consumidor de propor a ação no foro do seu domicílio, quando aplicável.` },
            ],
        },
        { id: 'contato', titulo: 'Contato', blocos: [CONTATO] },
    ],
};

const PRIVACIDADE: DocumentoLegal = {
    id: 'privacidade',
    caminho: '/politica-de-privacidade',
    titulo: 'Política de Privacidade',
    resumo: `Quais dados a ${PRODUTO.nome} coleta, para quê, com quem compartilha, por quanto tempo guarda e como você exerce os seus direitos.`,
    versao: VERSAO,
    vigenteDesde: VIGENCIA,
    secoes: [
        {
            id: 'escopo',
            titulo: 'A quem esta Política se aplica',
            blocos: [
                { tipo: 'p', texto: `Esta Política explica como a ${EMPRESA.razaoSocial} (CNPJ ${EMPRESA.cnpj}) trata dados pessoais na plataforma ${PRODUTO.nome} (${PRODUTO.site}). Tratamos dados em dois papéis diferentes:` },
                {
                    tipo: 'lista',
                    itens: [
                        'Como controladora, decidimos sobre os dados das clínicas e dos seus usuários necessários para criar e manter a conta, cobrar, dar suporte e proteger a plataforma. É desses dados que esta Política trata.',
                        `Como operadora, tratamos os dados que as clínicas inserem sobre pacientes e leads — inclusive dados de saúde — por conta e conforme as instruções de cada clínica, que é a controladora. Se você é paciente de uma clínica que usa a ${PRODUTO.nome}, a clínica é quem responde pelo tratamento dos seus dados; veja ${URL_LGPD}.`,
                    ],
                },
            ],
        },
        {
            id: 'dados',
            titulo: 'Dados que coletamos',
            blocos: [
                {
                    tipo: 'tabela',
                    colunas: ['Categoria', 'Dados', 'Origem'],
                    linhas: [
                        ['Conta', 'Nome, e-mail, telefone ou WhatsApp, papel na clínica e senha (guardada só como hash, que não permite recuperá-la)', 'Você ou a clínica que o convidou'],
                        ['Entrar com Google', 'Nome, e-mail e foto de perfil da conta Google', 'Google, quando você escolhe entrar com Google'],
                        ['Clínica e cobrança', 'Nome da clínica, CPF ou CNPJ, WhatsApp, endereço de cobrança, plano, faturas e histórico de pagamentos; do cartão, só a bandeira e os quatro últimos dígitos', 'Você, no cadastro, e o Asaas'],
                        ['Aceites', 'Versão dos Termos e da Política aceita, data, hora e endereço IP', 'Registro automático no aceite'],
                        ['Uso e segurança', 'Endereço IP, data e hora de acesso, navegador e registros de login e de ações relevantes na conta', 'Registro automático'],
                        ['Google Agenda', 'E-mail da conta Google conectada, o token de acesso concedido e os identificadores dos eventos criados pela plataforma', 'Google, quando o profissional conecta a agenda'],
                        ['Suporte', 'Mensagens e informações que você nos envia', 'Você'],
                    ],
                },
                { tipo: 'p', texto: 'Não usamos cookies de publicidade nem ferramentas de rastreamento de terceiros. O navegador guarda localmente apenas o necessário para manter você conectado e lembrar preferências, como o tema claro ou escuro.' },
            ],
        },
        {
            id: 'finalidades',
            titulo: 'Para que usamos os dados e com qual base legal',
            blocos: [
                {
                    tipo: 'tabela',
                    colunas: ['Finalidade', 'Base legal (LGPD)'],
                    linhas: [
                        ['Criar e manter a conta, dar acesso à equipe e prestar o serviço contratado', 'Execução de contrato (art. 7º, V)'],
                        ['Cobrar o plano, emitir faturas e processar pagamentos', 'Execução de contrato (art. 7º, V) e cumprimento de obrigação legal (art. 7º, II)'],
                        ['Enviar avisos sobre a conta, a assinatura, a segurança e mudanças no serviço', 'Execução de contrato (art. 7º, V)'],
                        ['Guardar registros de acesso à aplicação', 'Cumprimento de obrigação legal — Marco Civil da Internet, art. 15 (art. 7º, II)'],
                        ['Prevenir fraudes, proteger a plataforma e investigar incidentes', 'Legítimo interesse (art. 7º, IX)'],
                        ['Registrar o aceite dos Termos e da Política', 'Exercício regular de direitos (art. 7º, VI)'],
                        ['Atender pedidos de suporte', 'Execução de contrato (art. 7º, V)'],
                        ['Conectar e sincronizar o Google Agenda do profissional', 'Consentimento (art. 7º, I), revogável a qualquer momento ao desconectar'],
                        ['Melhorar a plataforma com estatísticas de uso agregadas', 'Legítimo interesse (art. 7º, IX)'],
                    ],
                },
                { tipo: 'p', texto: 'Não vendemos dados pessoais e não os usamos para publicidade de terceiros.' },
            ],
        },
        {
            id: 'compartilhamento',
            titulo: 'Com quem compartilhamos',
            blocos: [
                { tipo: 'p', texto: 'Compartilhamos dados apenas com os fornecedores necessários para operar a plataforma, que os tratam em nosso nome:' },
                { tipo: 'tabela', colunas: ['Fornecedor', 'Para quê'], linhas: FORNECEDORES.map(([nome, uso]) => [nome ?? '', uso ?? '']) },
                { tipo: 'p', texto: 'Também podemos compartilhar dados para cumprir ordem judicial ou requisição de autoridade competente, ou para exercer direitos em processos.' },
            ],
        },
        {
            id: 'internacional',
            titulo: 'Transferência internacional',
            blocos: [
                { tipo: 'p', texto: 'Os servidores da plataforma ficam no Brasil. Alguns fornecedores, como Google e OpenAI, tratam dados em outros países, principalmente nos Estados Unidos. Essas transferências ocorrem nas hipóteses do art. 33 da LGPD, com fornecedores que oferecem garantias contratuais de proteção de dados e medidas de segurança compatíveis com a lei brasileira.' },
            ],
        },
        {
            id: 'google',
            titulo: 'Dados recebidos do Google',
            blocos: [
                { tipo: 'p', texto: 'Quando você entra com Google, recebemos nome, e-mail e foto de perfil, usados apenas para identificar a sua conta.' },
                { tipo: 'p', texto: 'Quando um profissional conecta o Google Agenda, pedimos a permissão para gerenciar os eventos da própria agenda (calendar.events.owned). Usamos essa permissão somente para criar, atualizar e apagar, na agenda principal do profissional, os eventos que a própria plataforma gerou a partir dos atendimentos e bloqueios dele. Não lemos, copiamos nem guardamos os demais compromissos da agenda.' },
                {
                    tipo: 'lista',
                    itens: [
                        'O token de acesso fica guardado em cofre criptografado e só é usado pelo servidor para sincronizar a agenda.',
                        'Os eventos mostram apenas o primeiro nome do paciente; bloqueios aparecem como “Bloqueio”, sem o motivo.',
                        'Dados do Google não são vendidos, não são usados para publicidade e não são usados para treinar modelos de inteligência artificial.',
                        'Pessoas da nossa equipe não leem esses dados, salvo com a sua autorização, por segurança ou para cumprir a lei.',
                        'Você pode revogar o acesso a qualquer momento em Editar conta › Google Agenda › Desconectar, ou em https://myaccount.google.com/permissions. Ao desconectar pela plataforma, os eventos futuros criados por ela são apagados e o token é excluído.',
                    ],
                },
                { tipo: 'p', texto: `O uso e a transferência, para qualquer outro aplicativo, de informações recebidas das APIs do Google pela ${PRODUTO.nome} obedecem à Política de Dados do Usuário dos Serviços de API do Google (https://developers.google.com/terms/api-services-user-data-policy), incluindo os requisitos de Uso Limitado.` },
            ],
        },
        {
            id: 'retencao',
            titulo: 'Por quanto tempo guardamos',
            blocos: [
                {
                    tipo: 'tabela',
                    colunas: ['Dado', 'Prazo'],
                    linhas: [
                        ['Dados da conta e da clínica', 'Enquanto a contratação estiver ativa. Depois do encerramento: 30 dias para pedir a exportação, eliminação dos sistemas ativos em até 60 dias e das cópias de segurança em até 30 dias depois disso'],
                        ['Faturas e registros de pagamento', '5 anos, para cumprir obrigações fiscais'],
                        ['Registros de acesso à aplicação', 'No mínimo 6 meses, conforme o Marco Civil da Internet'],
                        ['Aceites dos Termos e da Política', 'Enquanto a conta existir e pelo prazo legal para o exercício de direitos'],
                        ['Conversas com o assistente de IA', '90 dias'],
                        ['Notificações da plataforma', 'Até 7 dias'],
                        ['Token do Google Agenda', 'Até o profissional desconectar a agenda ou revogar o acesso'],
                        ['Cópias de segurança', 'Rotação automática: as mais antigas são descartadas em até 30 dias'],
                    ],
                },
            ],
        },
        {
            id: 'seguranca',
            titulo: 'Como protegemos os dados',
            blocos: [
                {
                    tipo: 'lista',
                    itens: [
                        'Conexões sempre criptografadas (HTTPS/TLS).',
                        'Senhas guardadas só como hash; tokens, chaves e credenciais de integrações guardados em cofre criptografado.',
                        'Isolamento dos dados de cada clínica no banco de dados, com regras de acesso aplicadas no servidor a cada consulta.',
                        'Acesso do suporte a dados de clínicas apenas em modo somente leitura, por tempo limitado, com motivo registrado e auditoria.',
                        'Cópias de segurança diárias.',
                        'Acesso interno restrito às pessoas que precisam dele para operar a plataforma, sujeitas a dever de sigilo.',
                    ],
                },
                { tipo: 'p', texto: 'Nenhum sistema é totalmente imune a falhas. Se ocorrer um incidente de segurança que possa trazer risco ou dano relevante, comunicaremos os afetados e a Autoridade Nacional de Proteção de Dados (ANPD) nos prazos da regulamentação.' },
            ],
        },
        {
            id: 'direitos',
            titulo: 'Os seus direitos',
            blocos: [
                { tipo: 'p', texto: 'Nos termos dos arts. 18 e 20 da LGPD, você pode pedir, a qualquer momento:' },
                {
                    tipo: 'lista',
                    itens: [
                        'confirmação de que tratamos os seus dados e acesso a eles;',
                        'correção de dados incompletos, inexatos ou desatualizados;',
                        'anonimização, bloqueio ou eliminação de dados desnecessários, excessivos ou tratados em desconformidade com a lei;',
                        'portabilidade dos dados a outro fornecedor;',
                        'eliminação dos dados tratados com base no consentimento, e a revogação desse consentimento;',
                        'informação sobre com quem compartilhamos os seus dados e sobre a possibilidade de não consentir e as consequências disso;',
                        'oposição a tratamento feito em desconformidade com a lei;',
                        'revisão de decisões tomadas unicamente com base em tratamento automatizado.',
                    ],
                },
                { tipo: 'p', texto: `Para exercer os seus direitos, escreva ao Encarregado em ${ENCARREGADO.email}. Podemos pedir informações para confirmar a sua identidade. Respondemos em até 15 dias. Alguns dados podem ser mantidos quando a lei exigir.` },
                { tipo: 'p', texto: 'Se o pedido for sobre dados de paciente ou lead, ele deve ser feito à clínica, que é a controladora. Se recebermos o pedido, vamos encaminhá-lo à clínica responsável e dar a ela o apoio necessário para responder.' },
                { tipo: 'p', texto: 'Você também pode apresentar petição à ANPD em https://www.gov.br/anpd.' },
            ],
        },
        {
            id: 'criancas',
            titulo: 'Crianças e adolescentes',
            blocos: [
                { tipo: 'p', texto: 'A plataforma é destinada a profissionais e empresas, e os seus usuários devem ter pelo menos 18 anos. Dados de pacientes crianças ou adolescentes são inseridos pelas clínicas, que devem tratá-los no melhor interesse deles e conforme o art. 14 da LGPD.' },
            ],
        },
        {
            id: 'alteracoes',
            titulo: 'Alterações desta Política',
            blocos: [
                { tipo: 'p', texto: 'Podemos atualizar esta Política. A versão e a data de vigência ficam no topo desta página, e mudanças relevantes são avisadas pela plataforma ou por e-mail.' },
            ],
        },
        { id: 'contato', titulo: 'Encarregado e contato', blocos: [CONTATO] },
    ],
};

const LGPD: DocumentoLegal = {
    id: 'lgpd',
    caminho: '/lgpd',
    titulo: 'LGPD e Tratamento de Dados',
    resumo: `Como a ${PRODUTO.nome} divide responsabilidades com as clínicas, protege dados de saúde e atende os titulares — com o Acordo de Tratamento de Dados que vale entre a Fórmula 4IA e cada clínica.`,
    versao: VERSAO,
    vigenteDesde: VIGENCIA,
    secoes: [
        {
            id: 'compromisso',
            titulo: 'Nosso compromisso',
            blocos: [
                { tipo: 'p', texto: `A ${PRODUTO.nome} foi construída para clínicas de estética e saúde, que lidam com dados pessoais sensíveis todos os dias. Esta página resume como cumprimos a Lei Geral de Proteção de Dados (Lei nº 13.709/2018) e contém o Acordo de Tratamento de Dados que integra os Termos de Uso (${URL_TERMOS}).` },
            ],
        },
        {
            id: 'papeis',
            titulo: 'Quem é responsável por quê',
            blocos: [
                {
                    tipo: 'tabela',
                    colunas: ['Dados', 'Controladora', 'Operadora'],
                    linhas: [
                        ['Pacientes e leads: cadastro, contato, agendamentos, prontuários, fichas, fotos, anexos, orçamentos, vendas e mensagens', 'Clínica', 'Fórmula 4IA'],
                        ['Usuários da clínica: cadastro e acesso à conta', 'Fórmula 4IA', '—'],
                        ['Clínica: cadastro, assinatura e pagamentos', 'Fórmula 4IA', '—'],
                        ['Registros de acesso e segurança da plataforma', 'Fórmula 4IA', '—'],
                    ],
                },
                { tipo: 'p', texto: 'A controladora decide por que e como os dados são tratados; a operadora trata os dados em nome da controladora e segundo as instruções dela.' },
            ],
        },
        {
            id: 'saude',
            titulo: 'Dados de saúde',
            blocos: [
                { tipo: 'p', texto: 'Informações de prontuário, fichas de avaliação, fotos clínicas e anexos são dados pessoais sensíveis (art. 5º, II, e art. 11 da LGPD). Para tratá-los, a clínica normalmente se apoia na tutela da saúde, em procedimento realizado por profissionais ou serviços de saúde (art. 11, II, “f”), e no consentimento do paciente para finalidades que vão além do atendimento, como campanhas de marketing ou uso de imagens em divulgação.' },
                { tipo: 'p', texto: 'Cabe à clínica definir a base legal, informar os pacientes e registrar os consentimentos de que precisar. A plataforma ajuda assim:' },
                {
                    tipo: 'lista',
                    itens: [
                        'Os dados de cada clínica ficam isolados dos das demais, e a clínica define o papel de cada pessoa da equipe.',
                        'Cada atendimento guarda o modelo de ficha do dia em que foi feito: editar um modelo não reescreve prontuários antigos.',
                        'O suporte só enxerga dados da clínica em modo somente leitura, por até 30 minutos, com motivo registrado.',
                        'No Google Agenda do profissional, o evento traz apenas o primeiro nome do paciente.',
                        'Ações propostas pela inteligência artificial só são executadas depois da confirmação de uma pessoa.',
                    ],
                },
            ],
        },
        {
            id: 'acordo',
            titulo: 'Acordo de Tratamento de Dados',
            blocos: [
                { tipo: 'p', texto: 'Este Acordo integra os Termos de Uso e vale entre a Fórmula 4IA (operadora) e cada Clínica (controladora) para os dados de pacientes e leads tratados na plataforma. Ao aceitar os Termos de Uso, a Clínica aceita também este Acordo.' },
                {
                    tipo: 'lista',
                    itens: [
                        'Objeto e instruções. A Fórmula 4IA trata os dados apenas para prestar o serviço contratado e conforme as instruções da Clínica, expressas pelo uso das funções da plataforma e por pedidos formais ao suporte. Instruções que pareçam contrárias à lei serão comunicadas à Clínica.',
                        'Finalidade restrita. A Fórmula 4IA não usa os dados de pacientes e leads para finalidades próprias: não os vende, não os usa em publicidade e não os usa para treinar modelos de inteligência artificial.',
                        'Confidencialidade. Só têm acesso as pessoas que precisam dele para operar e dar suporte à plataforma, obrigadas a manter sigilo.',
                        'Segurança. A Fórmula 4IA mantém as medidas técnicas e administrativas descritas nesta página e na Política de Privacidade, adequadas à natureza sensível dos dados.',
                        'Suboperadores. A Clínica autoriza o uso dos fornecedores listados em “Fornecedores que tratam dados”. A Fórmula 4IA exige deles obrigações de proteção de dados compatíveis com este Acordo, responde pelos serviços que contrata e avisa as Clínicas com pelo menos 30 dias de antecedência antes de incluir um fornecedor que trate dados de pacientes.',
                        'Direitos dos titulares. Pedidos de pacientes recebidos pela Fórmula 4IA são encaminhados à Clínica. A plataforma permite consultar, corrigir e exportar os dados, e a Fórmula 4IA ajuda a Clínica a responder no prazo.',
                        'Incidentes. A Fórmula 4IA comunica à Clínica, em até 2 dias úteis depois de confirmar um incidente de segurança que envolva os dados dela, o que se sabe sobre o ocorrido, os dados e titulares afetados e as medidas tomadas, para que a Clínica possa fazer a comunicação à ANPD e aos titulares.',
                        'Transferência internacional. Recursos de IA e integrações do Google podem tratar dados fora do Brasil, nas condições descritas na Política de Privacidade.',
                        `Fim do contrato. ${FIM_DO_CONTRATO}`,
                        'Guarda do prontuário. A lei exige a guarda do prontuário por prazo longo (em regra, 20 anos, pela Lei nº 13.787/2018). Cabe à Clínica exportar e guardar os prontuários antes do fim da contratação; a Fórmula 4IA não mantém os dados além dos prazos acima.',
                        'Informações. Mediante pedido fundamentado, a Fórmula 4IA fornece à Clínica as informações necessárias para demonstrar o cumprimento deste Acordo.',
                        'Responsabilidade. Cada parte responde pelos danos que causar por descumprir a LGPD ou este Acordo, nos termos dos arts. 42 a 45 da LGPD e dos limites previstos nos Termos de Uso.',
                    ],
                },
            ],
        },
        {
            id: 'fornecedores',
            titulo: 'Fornecedores que tratam dados',
            blocos: [
                { tipo: 'tabela', colunas: ['Fornecedor', 'Para quê', 'Recebe dados de pacientes?'], linhas: FORNECEDORES },
            ],
        },
        {
            id: 'titulares',
            titulo: 'Como os titulares exercem os seus direitos',
            blocos: [
                {
                    tipo: 'lista',
                    itens: [
                        'Pacientes e leads: procure a clínica em que é atendido. Se escrever para nós, vamos encaminhar o pedido à clínica.',
                        `Usuários e clínicas: escreva ao Encarregado da Fórmula 4IA em ${ENCARREGADO.email}.`,
                        'Prazo: respondemos em até 15 dias.',
                        'Direitos: confirmação, acesso, correção, anonimização, bloqueio ou eliminação, portabilidade, informação sobre compartilhamento, revogação do consentimento, oposição e revisão de decisões automatizadas (arts. 18 e 20 da LGPD).',
                    ],
                },
            ],
        },
        {
            id: 'incidentes',
            titulo: 'Incidentes de segurança',
            blocos: [
                { tipo: 'p', texto: 'Confirmado um incidente com risco ou dano relevante aos titulares, comunicamos a ANPD e os titulares nos prazos da regulamentação, quando formos a controladora, e as clínicas afetadas em até 2 dias úteis, quando os dados forem delas.' },
            ],
        },
        {
            id: 'encarregado',
            titulo: 'Encarregado pelo tratamento de dados (DPO)',
            blocos: [
                {
                    tipo: 'lista',
                    itens: [
                        `Nome: ${ENCARREGADO.nome}`,
                        `E-mail: ${ENCARREGADO.email}`,
                        `Telefone e WhatsApp: ${ENCARREGADO.telefone}`,
                        `Controladora: ${QUALIFICACAO}`,
                    ],
                },
                { tipo: 'p', texto: 'Você também pode contatar a Autoridade Nacional de Proteção de Dados (ANPD) em https://www.gov.br/anpd.' },
            ],
        },
    ],
};

export const DOCUMENTOS: Record<IdDoDocumento, DocumentoLegal> = { termos: TERMOS, privacidade: PRIVACIDADE, lgpd: LGPD };

/** Na ordem em que aparecem nos rodapés. */
export const LISTA_DE_DOCUMENTOS: DocumentoLegal[] = [TERMOS, PRIVACIDADE, LGPD];
